/**
 * Zone 2: Tree Canopy Decorator
 *
 * Handles decoration for the Tree Canopy zone (Zone 2)
 * Includes 8 decoration types: 5 top-side + 3 underside
 *
 * Top decorations: small-bird, bird-nest, berries, moth, tree-moss
 * Underside decorations: hanging-vine, hanging-tendril, hanging-leaves
 *
 * PHASE 7C: Converted from Graphics to Sprites
 */

import Phaser from 'phaser';
import { PlatformDecoratorBase, DecorationConfig } from './PlatformDecoratorBase';
import { DecorationTextureGenerator } from '../DecorationTextureGenerator'; // PHASE 7C

export class Zone2Decorator extends PlatformDecoratorBase {
  protected getDecorations(): DecorationConfig[] {
    return [
      // Top-side decorations
      {
        type: 'small-bird',
        chance: 0.15,
        minWidth: 50,
        placement: 'top',
      },
      {
        type: 'bird-nest',
        chance: 0.1,
        minWidth: 70,
        placement: 'top',
      },
      {
        type: 'berries',
        chance: 0.18,
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'moth',
        chance: 0.12,
        minWidth: 45,
        placement: 'top',
      },
      {
        type: 'tree-moss',
        chance: 0.16,
        minWidth: 50,
        placement: 'top',
      },
      // Underside decorations
      {
        type: 'hanging-vine',
        chance: 0.4,
        minWidth: 50,
        placement: 'underside',
      },
      {
        type: 'hanging-tendril',
        chance: 0.35,
        minWidth: 40,
        placement: 'underside',
      },
      {
        type: 'hanging-leaves',
        chance: 0.3,
        minWidth: 60,
        placement: 'underside',
      },
    ];
  }

  /**
   * Add a specific decoration to the platform
   * PHASE 7C: CONVERTED TO SPRITES (was Graphics)
   */
  protected addDecoration(
    platformContainer: Phaser.GameObjects.Container,
    decorationType: string,
    _placement: 'top' | 'underside',
    width: number,
    height: number
  ): void {
    // PHASE 7C: Create sprite from pre-generated texture
    const textureKey = DecorationTextureGenerator.getTextureKey(decorationType);
    const sprite = this.scene.add.sprite(0, 0, textureKey);
    sprite.setOrigin(0.5, 0.5);
    platformContainer.add(sprite);

    const position = this.calculateDecorationPosition(decorationType, width, height);
    sprite.setPosition(position.x, position.y);

    const animationType = this.getAnimationType(decorationType);
    if (animationType) {
      this.animatedElements.push({
        sprite,
        type: animationType,
        animationPhase: this.random.nextFloat(0, Math.PI * 2),
        animationSpeed: this.getAnimationSpeed(animationType),
      });
    }
  }

  private calculateDecorationPosition(
    type: string,
    width: number,
    height: number
  ): { x: number; y: number } {
    // CRITICAL FIX: Only use platform-dependent positioning (constant offsets in texture)
    switch (type) {
      case 'small-bird':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the -6 offset
        };
      case 'bird-nest':
        return {
          x: this.random.nextFloat(-width * 0.2, width * 0.2),
          y: -height / 2, // Texture has the -3 offset
        };
      case 'berries':
        return {
          x: this.random.nextFloat(-width * 0.35, width * 0.35),
          y: -height / 2, // Texture has the -5 offset
        };
      case 'moth':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the random -12 to -20 offset
        };
      case 'tree-moss': {
        const side = this.random.next() > 0.5 ? 1 : -1;
        return {
          x: (side * width) / 2,
          y: -height / 2, // Texture has the random -2 to +2 offset
        };
      }
      case 'hanging-vine':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: height / 2,
        };
      case 'hanging-tendril':
        return {
          x: this.random.nextFloat(-width * 0.4, width * 0.4),
          y: height / 2,
        };
      case 'hanging-leaves':
        return {
          x: this.random.nextFloat(-width * 0.25, width * 0.25),
          y: height / 2,
        };
      default:
        return { x: 0, y: 0 };
    }
  }

  private getAnimationType(type: string): string | null {
    switch (type) {
      case 'moth':
        return 'moth-hover';
      default:
        return null;
    }
  }

  private getAnimationSpeed(type: string): number {
    switch (type) {
      case 'moth-hover':
        return 0.1;
      default:
        return 0.05;
    }
  }
}
